
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';

interface SiteConfiguration {
  id: string;
  key: string;
  value: string;
  display_name: string;
  description?: string;
  category: 'forms_links' | 'image_generation' | 'user_management' | 'system_health' | 'ui_settings' | 'security';
  data_type: 'string' | 'number' | 'boolean' | 'json' | 'url';
  validation_rules: any;
  is_required: boolean;
  updated_by?: string;
  created_at: string;
  updated_at?: string;
}

export const useSiteConfig = () => {
  const [configurations, setConfigurations] = useState<SiteConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useSecureAuthContext();

  const fetchConfigurations = async () => {
    try {
      setLoading(true);
      // Use secure RPC function that masks admin emails for non-service-role users
      const { data, error } = await supabase
        .rpc('get_safe_site_configurations');

      if (error) throw error;
      
      // Sort by category after fetching
      const sortedData = (data || []).sort((a: any, b: any) => 
        a.category.localeCompare(b.category)
      );
      
      setConfigurations(sortedData);
    } catch (err) {
      console.error('Error fetching site configurations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch configurations');
    } finally {
      setLoading(false);
    }
  };

  const updateConfiguration = async (key: string, value: string, changeReason?: string) => {
    try {
      const { error } = await supabase.rpc('update_site_config', {
        config_key: key,
        new_value: value,
        admin_email: user?.email,
        change_reason: changeReason
      });

      if (error) throw error;
      
      // Update local state
      setConfigurations(prev => 
        prev.map(config => 
          config.key === key ? { ...config, value, updated_at: new Date().toISOString() } : config
        )
      );
    } catch (err) {
      console.error('Error updating configuration:', err);
      throw err;
    }
  };

  const addConfiguration = async (newConfig: Omit<SiteConfiguration, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const configWithDefaults = {
        ...newConfig,
        validation_rules: newConfig.validation_rules || {}
      };

      const { data, error } = await supabase
        .from('site_configurations')
        .insert(configWithDefaults)
        .select()
        .single();

      if (error) throw error;
      
      setConfigurations(prev => [...prev, data]);
      return data;
    } catch (err) {
      console.error('Error adding configuration:', err);
      throw err;
    }
  };

  const deleteConfiguration = async (key: string) => {
    try {
      const { error } = await supabase
        .from('site_configurations')
        .delete()
        .eq('key', key);

      if (error) throw error;
      
      setConfigurations(prev => prev.filter(config => config.key !== key));
    } catch (err) {
      console.error('Error deleting configuration:', err);
      throw err;
    }
  };

  const getConfiguration = (key: string): string | null => {
    const config = configurations.find(c => c.key === key);
    return config?.value || null;
  };

  const resetToDefault = async (key: string) => {
    try {
      // You could implement default value logic here
      // For now, this is a placeholder
      console.log('Reset to default for:', key);
    } catch (err) {
      console.error('Error resetting configuration:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchConfigurations();

    // Set up real-time subscription with improved fallback
    const channel = supabase
      .channel(`site-config-changes-${Date.now()}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'site_configurations'
      }, () => {
        fetchConfigurations();
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('⚠️ Site config real-time subscription failed, using polling fallback');
          // Fallback to polling every 2 minutes for config changes
          const pollInterval = setInterval(fetchConfigurations, 120000);
          return () => clearInterval(pollInterval);
        }
      });

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error('Error cleaning up site config subscription:', error);
      }
    };
  }, []);

  return {
    configurations,
    loading,
    error,
    updateConfiguration,
    addConfiguration,
    deleteConfiguration,
    getConfiguration,
    resetToDefault,
    refetch: fetchConfigurations
  };
};
